import { useEffect, useRef, useState } from 'react';
import type { Proyecto } from '../../nucleo/dominio/espacio';
import {
  COLORES_DE_CARPETA,
  ESPACIO_DE_TRABAJO_PROPIO,
  VERSION_ESQUEMA_ORGANIZACION,
  type Carpeta,
  type EspacioDeTrabajo,
} from '../../nucleo/dominio/organizacion';
import { fechaHora } from '../formato';
import { M } from '../mensajes/es-PE';
import { FondoDialogo } from '../ui/Dialogo';
import { Icono } from '../ui/Icono';
import { confirmar } from '../ui/Confirmaciones';

const O = M.organizacion;

/** Insignia «Datos reales» (con la explicación al pasar el cursor). */
export function InsigniaDatosReales({ largo = false }: { largo?: boolean }) {
  return (
    <span className="insignia datos-reales" title={O.datosRealesAyuda}>
      {largo ? O.datosRealesLargo : O.datosReales}
    </span>
  );
}

function useFoco<T extends HTMLElement>() {
  const ref = useRef<T>(null);
  useEffect(() => ref.current?.focus(), []);
  return ref;
}

/**
 * Crear o configurar un espacio de trabajo: nombre, tipo y la marca «contiene datos reales» (quitar
 * la marca pide confirmación). Eliminar solo si está vacío y no es «Mis proyectos».
 */
export function DialogoEspacioDeTrabajo({
  espacio,
  ahora,
  nuevoId,
  alGuardar,
  alEliminar,
  alCerrar,
}: {
  espacio: EspacioDeTrabajo | null;
  ahora: string;
  nuevoId: () => string;
  alGuardar: (e: EspacioDeTrabajo) => void;
  alEliminar: (e: EspacioDeTrabajo) => void;
  alCerrar: () => void;
}) {
  const [nombre, setNombre] = useState(espacio?.nombre ?? '');
  const [tipo, setTipo] = useState(espacio?.tipo ?? 'cliente');
  const [datosReales, setDatosReales] = useState(espacio?.contieneDatosReales ?? false);
  const foco = useFoco<HTMLInputElement>();
  const propio = espacio?.id === ESPACIO_DE_TRABAJO_PROPIO;

  const guardar = async () => {
    const limpio = nombre.trim();
    if (!limpio) return;
    if (
      espacio?.contieneDatosReales &&
      !datosReales &&
      !(await confirmar({
        titulo: O.quitarMarcaTitulo,
        texto: O.quitarMarcaTexto,
        confirmar: O.quitarMarca,
        peligro: true,
      }))
    )
      return;
    alGuardar({
      versionEsquema: VERSION_ESQUEMA_ORGANIZACION,
      id: espacio?.id ?? nuevoId(),
      nombre: limpio,
      tipo: propio ? 'propio' : tipo,
      contieneDatosReales: datosReales,
      creadoEn: espacio?.creadoEn || ahora,
      actualizadoEn: ahora,
    });
  };

  return (
    <FondoDialogo alCerrar={alCerrar}>
      <form
        className="panel dialogo dialogo-organizacion"
        role="dialog"
        aria-modal="true"
        aria-labelledby="titulo-espacio-trabajo"
        data-testid="dialogo-espacio-trabajo"
        onSubmit={(e) => {
          e.preventDefault();
          void guardar();
        }}
      >
        <h2 id="titulo-espacio-trabajo">{O.espacioTitulo(espacio === null)}</h2>
        <label className="campo">
          <span>{O.nombre}</span>
          <input
            ref={foco}
            className="entrada"
            value={nombre}
            required
            maxLength={60}
            placeholder={O.nombreEspacioEjemplo}
            onChange={(e) => setNombre(e.target.value)}
          />
        </label>
        {!propio ? (
          <label className="campo">
            <span>{O.tipo}</span>
            <select
              className="entrada"
              value={tipo}
              onChange={(e) => setTipo(e.target.value as EspacioDeTrabajo['tipo'])}
            >
              <option value="cliente">{O.tipoCliente}</option>
              <option value="propio">{O.tipoPropio}</option>
            </select>
          </label>
        ) : null}
        {!propio ? (
          <label className="campo-casilla marca-datos-reales">
            <input
              type="checkbox"
              checked={datosReales}
              onChange={(e) => setDatosReales(e.target.checked)}
            />
            <span>
              <strong>{O.marcaDatosReales}</strong>
              <span className="secundario">{O.marcaDatosRealesAyuda}</span>
            </span>
          </label>
        ) : (
          <p className="secundario">{O.espacioPropioFijo}</p>
        )}
        <div className="acciones">
          {espacio && !propio ? (
            <button
              type="button"
              className="boton peligro fantasma-izq"
              onClick={() => alEliminar(espacio)}
            >
              <Icono nombre="eliminar" tamano={16} />
              {O.eliminarEspacio}
            </button>
          ) : null}
          <button type="button" className="boton" onClick={alCerrar}>
            {M.app.cancelar}
          </button>
          <button type="submit" className="boton principal">
            {espacio ? O.guardar : O.crearEspacio}
          </button>
        </div>
      </form>
    </FondoDialogo>
  );
}

/** Crear o editar una carpeta: nombre y color; eliminarla deja sus proyectos «Sin carpeta». */
export function DialogoCarpeta({
  carpeta,
  espacioDeTrabajoId,
  sugerido,
  ahora,
  nuevoId,
  alGuardar,
  alEliminar,
  alCerrar,
}: {
  carpeta: Carpeta | null;
  espacioDeTrabajoId: string;
  sugerido: string;
  ahora: string;
  nuevoId: () => string;
  alGuardar: (c: Carpeta) => void;
  alEliminar: (c: Carpeta) => void;
  alCerrar: () => void;
}) {
  const [nombre, setNombre] = useState(carpeta?.nombre ?? '');
  const [color, setColor] = useState(carpeta?.color ?? sugerido);
  const foco = useFoco<HTMLInputElement>();
  return (
    <FondoDialogo alCerrar={alCerrar}>
      <form
        className="panel dialogo dialogo-organizacion"
        role="dialog"
        aria-modal="true"
        aria-labelledby="titulo-carpeta"
        data-testid="dialogo-carpeta"
        onSubmit={(e) => {
          e.preventDefault();
          const limpio = nombre.trim();
          if (!limpio) return;
          alGuardar({
            versionEsquema: VERSION_ESQUEMA_ORGANIZACION,
            id: carpeta?.id ?? nuevoId(),
            espacioDeTrabajoId: carpeta?.espacioDeTrabajoId ?? espacioDeTrabajoId,
            nombre: limpio,
            color,
            creadoEn: carpeta?.creadoEn ?? ahora,
          });
        }}
      >
        <h2 id="titulo-carpeta">{O.carpetaTitulo(carpeta === null)}</h2>
        <label className="campo">
          <span>{O.nombre}</span>
          <input
            ref={foco}
            className="entrada"
            value={nombre}
            required
            maxLength={60}
            placeholder={O.nombreCarpetaEjemplo}
            onChange={(e) => setNombre(e.target.value)}
          />
        </label>
        <fieldset className="campo colores-carpeta">
          <legend>{O.color}</legend>
          <div className="fila-colores">
            {COLORES_DE_CARPETA.map((c, i) => (
              <label key={c} className="muestra-color" title={O.colorN(i + 1)}>
                <input
                  type="radio"
                  name="color-carpeta"
                  value={c}
                  checked={color === c}
                  aria-label={O.colorN(i + 1)}
                  onChange={() => setColor(c)}
                />
                <span style={{ background: c }} />
              </label>
            ))}
          </div>
        </fieldset>
        <div className="acciones">
          {carpeta ? (
            <button
              type="button"
              className="boton peligro fantasma-izq"
              onClick={() => alEliminar(carpeta)}
            >
              <Icono nombre="eliminar" tamano={16} />
              {O.eliminarCarpeta}
            </button>
          ) : null}
          <button type="button" className="boton" onClick={alCerrar}>
            {M.app.cancelar}
          </button>
          <button type="submit" className="boton principal">
            {carpeta ? O.guardar : O.crearCarpeta}
          </button>
        </div>
      </form>
    </FondoDialogo>
  );
}

export interface CambiosDeProyecto {
  nombre: string;
  espacioDeTrabajoId: string;
  carpetaId: string | null;
}

/**
 * «Opciones del proyecto» (como el menú del proyecto del v6): renombrar, mover a otro espacio de
 * trabajo o carpeta, estadísticas, duplicar, exportar y eliminar.
 */
export function DialogoOpcionesProyecto({
  proyecto,
  espacios,
  carpetasDe,
  alGuardar,
  alDuplicar,
  alExportar,
  alEliminar,
  alCerrar,
}: {
  proyecto: Proyecto;
  espacios: EspacioDeTrabajo[];
  carpetasDe: (espacioDeTrabajoId: string) => Promise<Carpeta[]>;
  alGuardar: (c: CambiosDeProyecto) => void;
  alDuplicar: () => void;
  alExportar: () => void;
  alEliminar: () => void;
  alCerrar: () => void;
}) {
  const [nombre, setNombre] = useState(proyecto.nombre);
  const [espacioId, setEspacioId] = useState(proyecto.espacioDeTrabajoId);
  const [carpetaId, setCarpetaId] = useState(proyecto.carpetaId ?? '');
  const [carpetas, setCarpetas] = useState<Carpeta[]>([]);
  const foco = useFoco<HTMLInputElement>();

  useEffect(() => {
    let vigente = true;
    void carpetasDe(espacioId).then((c) => vigente && setCarpetas(c));
    return () => {
      vigente = false;
    };
  }, [espacioId, carpetasDe]);

  const origen = espacios.find((e) => e.id === proyecto.espacioDeTrabajoId);
  const elevadores = proyecto.espacios.reduce(
    (a, e) => a + e.layout.elementos.filter((x) => x.catalogoId === 'elevador').length,
    0,
  );

  return (
    <FondoDialogo alCerrar={alCerrar}>
      <form
        className="panel dialogo dialogo-organizacion"
        role="dialog"
        aria-modal="true"
        aria-labelledby="titulo-opciones"
        data-testid="dialogo-opciones-proyecto"
        onSubmit={(e) => {
          e.preventDefault();
          alGuardar({
            nombre: nombre.trim() || proyecto.nombre,
            espacioDeTrabajoId: espacioId,
            carpetaId: carpetaId || null,
          });
        }}
      >
        <h2 id="titulo-opciones">{O.opcionesTitulo}</h2>
        <label className="campo">
          <span>{O.nombre}</span>
          <input
            ref={foco}
            className="entrada"
            value={nombre}
            required
            maxLength={80}
            onChange={(e) => setNombre(e.target.value)}
          />
        </label>
        <label className="campo">
          <span>{O.espacioDeTrabajo}</span>
          <select
            className="entrada"
            value={espacioId}
            onChange={(e) => {
              setEspacioId(e.target.value);
              setCarpetaId('');
            }}
          >
            {espacios.map((e) => (
              <option
                key={e.id}
                value={e.id}
                disabled={!!origen?.contieneDatosReales && !e.contieneDatosReales}
              >
                {e.nombre}
                {e.contieneDatosReales ? ` · ${O.datosReales}` : ''}
              </option>
            ))}
          </select>
        </label>
        {origen?.contieneDatosReales ? (
          <p className="secundario nota-datos-reales">{O.noSaleDeDatosReales}</p>
        ) : null}
        <label className="campo">
          <span>{O.carpeta}</span>
          <select
            className="entrada"
            value={carpetaId}
            onChange={(e) => setCarpetaId(e.target.value)}
          >
            <option value="">{O.sinCarpeta}</option>
            {carpetas.map((c) => (
              <option key={c.id} value={c.id}>
                {c.nombre}
              </option>
            ))}
          </select>
        </label>
        <p className="atenuado num">
          {O.estadisticas(
            proyecto.espacios.length,
            elevadores,
            fechaHora(proyecto.creadoEn),
            fechaHora(proyecto.actualizadoEn),
          )}
        </p>
        <div className="acciones-secundarias">
          <button type="button" className="boton" onClick={alDuplicar}>
            {O.duplicar}
          </button>
          <button type="button" className="boton" onClick={alExportar}>
            <Icono nombre="exportar" tamano={16} />
            {M.inicio.exportar}
          </button>
          <button type="button" className="boton peligro" onClick={alEliminar}>
            <Icono nombre="eliminar" tamano={16} />
            {M.inicio.eliminar}
          </button>
        </div>
        <div className="acciones">
          <button type="button" className="boton" onClick={alCerrar}>
            {M.app.cancelar}
          </button>
          <button type="submit" className="boton principal">
            {O.guardar}
          </button>
        </div>
      </form>
    </FondoDialogo>
  );
}
